import { StyleSheet, View, ViewProps } from 'react-native'
import { Button, Spacing, Text } from 'zens-storybook'
import { Draft, UserData, useUser } from 'zens-store'
import { useTranslation } from 'react-i18next'
import { useMemo } from 'react'

import { Panel } from '../layout'
import { InfoBlock } from '../info'

import UserRoleInfo from './roleinfo'
import UserInfo from './info'

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  bottom: {
    flexDirection: 'row',
    marginHorizontal: -Spacing.XS,
    justifyContent: 'flex-end',
  },
  button: {
    marginHorizontal: Spacing.XS,
  },
  skeletonRow: {
    height: 32,
  },
  skeletonInfo: {
    marginBottom: Spacing.L,
  },
  skeletonRoles: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  skeletonText: {
    minWidth: 200,
  },
})

interface Props extends ViewProps {
  companyId: number
  userId: number
  onRevoke?: (draft: Draft<UserData>) => void
}

const UserDetail = (props: Props) => {
  const { companyId, userId, onRevoke, style, ...otherProps } = props
  const { t } = useTranslation()
  const user = useUser({ companyId, userId })
  const draft = useMemo(() => new Draft<UserData>({ id: userId, ...user.data?.data }), [user.data?.data])

  return (
    <View style={[styles.container, style]} {...otherProps}>
      {user.data && (
        <>
          <UserInfo user={user.data} companyId={companyId} />
          <Panel title={t('Roles')}>
            <UserRoleInfo user={user.data} companyId={companyId} />
          </Panel>
        </>
      )}

      <View style={styles.bottom}>
        <Button
          title={t('Remove user from company')}
          size="L"
          style={styles.button}
          onPress={() => onRevoke?.(draft)}
          testID="UserDetail.Revoke"
          danger
        />
      </View>
    </View>
  )
}

interface SkeletonProps extends ViewProps {}
const Skeleton = (props: SkeletonProps) => {
  const { style, ...otherProps } = props
  const { t } = useTranslation()
  return (
    <View style={[styles.container, style]} {...otherProps}>
      <InfoBlock.Skeleton style={styles.skeletonInfo}>
        <Text.Skeleton style={styles.skeletonRow} />
        <Text.Skeleton style={styles.skeletonRow} />
        <Text.Skeleton style={styles.skeletonRow} />
      </InfoBlock.Skeleton>
      <Panel title={t('Roles')}>
        <InfoBlock.Skeleton style={styles.skeletonInfo}>
          <View style={styles.skeletonRoles}>
            <Text.Skeleton style={styles.skeletonText} />
            <Button.Skeleton />
          </View>
        </InfoBlock.Skeleton>
      </Panel>
      <View style={styles.bottom}>
        <Button.Skeleton style={styles.button} />
      </View>
    </View>
  )
}
UserDetail.Skeleton = Skeleton

export default UserDetail
